import { redirect } from '@tanstack/react-router'
import Cookies from 'js-cookie'
import { create } from 'zustand'

import { queryClient } from '@/lib/tanstack-query'
import { getProfileAccount } from '../profile/profile.util'

const COOKIE_NAME = 'yes_auth'

type Value = {
  isAuthenticated: boolean
  // user: GetProfileQuery['getProfile']['user'] | null
}

interface Actions {
  login(token: string): Promise<Value>
  logout(): Promise<void>
  hydrate(): Promise<void>
  getCookieToken(): string | null
  setCookieToken(token: string): void
  removeCookieToken(): void
  access(type?: 'authenticated', redirectBack?: boolean): void
}

const initialValue: Value = {
  isAuthenticated: false,
}

async function getProfileWithCatch() {
  try {
    return await getProfileAccount()
  } catch (error) {
    console.debug('🚀 ~ getProfileWithCatch ~ error:', error)
    return null
  }
}

const useAuth = create<Value & Actions>((set, get) => ({
  ...initialValue,
  async login(token) {
    get().setCookieToken(token)

    set({ isAuthenticated: true })
    return get()
  },
  async hydrate() {
    const { getCookieToken, logout } = get()
    const token = getCookieToken()

    if (token) {
      const profile = await getProfileWithCatch()
      if (profile) {
        return set({ isAuthenticated: true })
      }

      await logout()
    }

    set(initialValue)
  },
  async logout() {
    queryClient.clear()
    get().removeCookieToken()
    set(initialValue)
  },
  getCookieToken() {
    return Cookies.get(COOKIE_NAME) || null
  },
  setCookieToken(token) {
    Cookies.set(COOKIE_NAME, token)
  },
  removeCookieToken() {
    Cookies.remove(COOKIE_NAME)
  },
  access(type = 'authenticated', redirectBack = false) {
    const { isAuthenticated } = get()
    const isAllowed = type === 'authenticated' ? isAuthenticated : true

    let redirectTo = undefined

    if (redirectBack) {
      redirectTo = window.location.pathname
    }

    if (!isAllowed)
      throw redirect({
        to: '/auth/login',
        replace: true,
        search: { redirect: redirectTo },
      })
  },
}))

export { useAuth }
